import React, {useEffect, useState} from 'react';
import SuggestedAddressModal from 'shared/account-settings/components/address-verification-modals/suggested-address-modal';
import CannotVerifyAddressModal from 'shared/account-settings/components/address-verification-modals/cannot-verify-address-modal';
import {useFormikContext} from 'formik';
import {useBoolean} from '../../hooks/useBoolean';
import {
  SharedAccountSettings,
  useAccountSettingsContext,
} from '../AccountSettings/AccountSettingsContext';
import AssetService from '../../services/AssetService';
import {Address} from '../../types/user';
import useAsyncEffect from '../../hooks/use-async-effect';

const AddressVerificationModal = () => {
  const showModal = useBoolean(false);
  const wasSubmitting = useBoolean(false);
  const verifying = useBoolean(false);
  const formik = useFormikContext<Address>();
  const accountSettingsContext = useAccountSettingsContext<Address & SharedAccountSettings>();
  const [suggestedAddress, setSuggestedAddress] = useState<Address | null>(null);
  const originalAddress = {
    addressLine1: formik.values.addressLine1,
    addressLine2: formik.values.addressLine2,
    city: formik.values.city,
    subdivision: formik.values.subdivision,
    postalCode: formik.values.postalCode,
    addressVerified: formik.values.addressVerified,
    confirmedUnverifiedAddress: formik.values.confirmedUnverifiedAddress,
  };

  const submitForm = async (values) => {
    await accountSettingsContext.saveSettings(values);
    formik.setValues({...formik.values, ...values});
    verifying.setFalse();
  };

  const handleAddressVerification = async () => {
    try {
      const suggestion = await AssetService.verifyAddress(formik.values);

      if (suggestion && suggestion.addressVerified) {
        await submitForm({
          ...suggestion,
          addressVerified: true,
          confirmedUnverifiedAddress: false,
        });
      } else {
        showModal.setTrue();
        setSuggestedAddress(suggestion ? suggestion : null);
      }
    } catch {
      showModal.setTrue();
    }
  };

  useEffect(() => {
    if (verifying.value) {
      return;
    }

    const {
      values: {
        addressLine1,
        addressLine2,
        city,
        subdivision,
        postalCode,
        addressVerified,
        confirmedUnverifiedAddress,
      },
    } = formik;

    const conditions = [
      addressLine1 !== formik.initialValues.addressLine1,
      addressLine2 !== formik.initialValues.addressLine2,
      city !== formik.initialValues.city,
      subdivision !== formik.initialValues.subdivision,
      postalCode !== formik.initialValues.postalCode,
    ];
    const addressChanged = conditions.some(Boolean);

    if (!addressChanged) {
      return;
    }

    if (addressVerified) {
      formik.setFieldValue('addressVerified', false);
    }
    if (confirmedUnverifiedAddress) {
      formik.setFieldValue('confirmedUnverifiedAddress', false);
    }
  }, [
    formik.values.addressLine1,
    formik.values.addressLine2,
    formik.values.city,
    formik.values.subdivision,
    formik.values.postalCode,
    verifying.value,
  ]);

  useAsyncEffect(async () => {
    if (verifying.value) {
      return;
    }

    // Only pop if the form was submitted validly
    if (!formik.isValid) {
      wasSubmitting.setFalse();
      return;
    }

    if (formik.isSubmitting && !wasSubmitting.value) {
      wasSubmitting.setTrue();
    } else if (!formik.isSubmitting && wasSubmitting.value) {
      if (!formik.values.addressVerified && !formik.values.confirmedUnverifiedAddress) {
        verifying.setTrue();
        await handleAddressVerification();
      }

      wasSubmitting.setFalse();
    }
  }, [
    formik.isSubmitting,
    wasSubmitting.value,
    formik.values.addressVerified,
    formik.values.confirmedUnverifiedAddress,
    verifying.value,
  ]);

  const tryAgain = () => {
    ['city', 'subdivision', 'postalCode'].forEach((field) => {
      formik.setFieldValue(field, '');
    });

    formik.setFieldError('addressLine1', 'We could not verify your address. Please try again.');

    formik.setFieldTouched('addressLine1', true, true);

    showModal.setFalse();
    verifying.setFalse();
  };

  if (!showModal.value) {
    return null;
  }

  return (
    <>
      {suggestedAddress ? (
        <SuggestedAddressModal
          show
          closeModal={showModal.setFalse}
          suggestedAddress={suggestedAddress}
          submitForm={submitForm}
          originalAddress={originalAddress}
        />
      ) : (
        <CannotVerifyAddressModal
          show
          closeModal={showModal.setFalse}
          tryAgain={tryAgain}
          submitForm={submitForm}
          originalAddress={originalAddress}
        />
      )}
    </>
  );
};

export default AddressVerificationModal;
